import type { InputRequirement } from "@intelligems/ig-types/schemas/ai/nodes/input-requirement";
import type { UserInputAnswer } from "@intelligems/ig-types/schemas/ai/node-execution";

/**
 * Where a stepped survey card's unsubmitted answers are kept, so a reload of a
 * restored session puts the merchant back on the question they had reached.
 *
 * Everything the merchant SUBMITTED is in the server's transcript; only the
 * answers still inside the card — the steps before the last one — exist
 * nowhere else. They are kept per session (one entry, overwritten when the
 * session parks on a new question) in `sessionStorage`, which is scoped to the
 * tab and gone when it closes, and dropped the moment the card submits.
 *
 * The stored progress names the requirement it was recorded against, and is
 * only read back for that same requirement: answers to a question the session
 * has since moved past must never pre-fill the next one.
 */
export interface SurveyProgress {
  currentIndex: number;
  answers: Record<number, UserInputAnswer>;
}

interface StoredSurveyProgress extends SurveyProgress {
  requirement: string;
}

const keyFor = (sessionId: string) => `ig:atlas-survey-progress:${sessionId}`;

/**
 * Storage can be unavailable (a privacy mode, a full quota) or hold something
 * this version did not write; either way the card simply starts fresh.
 */
export const readSurveyProgress = (
  storage: Storage | null,
  sessionId: string,
  requirement: InputRequirement
): SurveyProgress | null => {
  if (!storage || !sessionId) {
    return null;
  }
  try {
    const raw = storage.getItem(keyFor(sessionId));
    if (!raw) {
      return null;
    }
    const stored = JSON.parse(raw) as Partial<StoredSurveyProgress>;
    if (
      stored.requirement !== JSON.stringify(requirement) ||
      typeof stored.currentIndex !== "number" ||
      !stored.answers ||
      typeof stored.answers !== "object"
    ) {
      return null;
    }
    return { currentIndex: stored.currentIndex, answers: stored.answers };
  } catch {
    return null;
  }
};

export const writeSurveyProgress = (
  storage: Storage | null,
  sessionId: string,
  requirement: InputRequirement,
  progress: SurveyProgress
) => {
  if (!storage || !sessionId) {
    return;
  }
  const stored: StoredSurveyProgress = {
    requirement: JSON.stringify(requirement),
    ...progress,
  };
  try {
    storage.setItem(keyFor(sessionId), JSON.stringify(stored));
  } catch {
    // Best effort: the answers are still on screen, only a reload loses them.
  }
};

export const clearSurveyProgress = (
  storage: Storage | null,
  sessionId: string
) => {
  if (!storage || !sessionId) {
    return;
  }
  try {
    storage.removeItem(keyFor(sessionId));
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
};
